import "./index.css";
import React, { useMemo } from "react";
import { Badge, Spin, Tabs } from "antd";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import ShopType from "@/enum/ShopType";
import ShopDTO from "@/dto/shop/ShopDTO";
import { useHistory } from "react-router";
import OrderSyncButton from "../../OrderSyncButton";
import useResetTab from "./hooks/useResetTab";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import { observer } from "mobx-react-lite";
import OrderTabNameMap from "@/scenes/OrderManagement/map/OrderTabNameMap";
import SupplierSyncDropButton from "../../OrderSyncButton/SupplierSyncDropButton";
import { PremiumFunctionType, usePremiumValidator } from "@/services/PremiumValidator";
import SubscriptionEndTime from "./SubscriptionEndTime";
import PremiumVersionIcon from "@/components/PremiumVersionIcon";
import { collectEvent } from "@/services/dataTrack";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import ShopTypeNameMap from "@/map/ShopTypeNameMap";
import { isJdDropshipShop } from "@/utils/ShopUtils";
import { TabItem, buildTabsItems } from "@/utils/antdItems";

interface OrderTabsProps {
    shop: ShopDTO;

    tab: OrderTab;

    isSupplierShop?: boolean;
}

const OrderTabs: React.FC<OrderTabsProps> = function (props) {
    const orderManagementStore = useOrderManagementStore();
    const { validate: handleValidate, loading } = usePremiumValidator();
    const { shop, tab: currentTab, isSupplierShop } = props;

    const history = useHistory();

    useResetTab(currentTab, shop.id);

    const {
        totalPendingShipmentOrders,
        totalOnHoldOrders,
        totalPendingShipmentRefundingOrders,
        hasAbnormalOrders,
        totalLogisticsWarningOrders,
        preShipOrders,
        pickupDeadlineWarningOrderCount,
    } = orderManagementStore?.orderListCount || {};

    const shopType = shop.type;

    const onChange = async (tab: OrderTab) => {
        // 如果是物流异常预警，并且不是高级版限制跳转
        if (tab === OrderTab.LOGISTICS_WARNING) {
            collectEvent(DataTrackEvent.CHANGE_ORDER_TAB_TO_LOGISTICS_WARN, {
                shop_id: shop.id,
                shop_name: shop.name,
                shop_type: ShopTypeNameMap[shop.type],
            });

            /** 如果有发货后超时揽收预警订单，则优先跳到发货后超时揽收预警 */
            if (pickupDeadlineWarningOrderCount) {
                history.push(`/order/list?shopId=${shop.id}&tab=${OrderTab.PICKUP_DEADLINE_WARNING}`);
                return;
            } else {
                if (loading) {
                    return;
                }

                const shouldNext = await handleValidate({
                    type: PremiumFunctionType.LOGISTICS_WARNING_ORDER_LIST,
                    shopId: shop.id,
                });

                if (!shouldNext) {
                    return;
                }
            }
        }

        history.push(`/order/list?shopId=${shop.id}&tab=${tab}`);
    };

    // tabBar右侧的同步订单按钮 显示隐藏逻辑
    const tabBarExtraContent = useMemo(() => {
        const hiddenOrderSyncShopTypes = [ShopType.INDEPENDENT_MANUAL];

        if (hiddenOrderSyncShopTypes.includes(shop.type)) {
            return null;
        }

        let right: JSX.Element;

        if (isSupplierShop) {
            right = <SupplierSyncDropButton supplierShopId={shop.id} tab={currentTab} />;
        } else {
            if (shop.type === ShopType.MANUAL) {
                right = <SubscriptionEndTime shop={shop} />;
            } else {
                right = <OrderSyncButton shop={shop} />;
            }
        }

        return {
            right,
        };
    }, [shop, currentTab, isSupplierShop]);

    const isPreShipWhiteShop = [ShopType.DOUDIAN, ShopType.TAOBAO, ShopType.PINDUODUO].includes(shop.type);

    function buildLogisticsWarningTab() {
        const isPdd = [ShopType.PDD_FDS, ShopType.PINDUODUO].includes(shop.type);

        const shouldRender = isPdd
            ? totalLogisticsWarningOrders || pickupDeadlineWarningOrderCount
            : totalLogisticsWarningOrders > 0;

        if (!shouldRender) return null;

        const badgeCount = isPdd
            ? [totalLogisticsWarningOrders, pickupDeadlineWarningOrderCount].filter((num) => num !== 0).join(" + ")
            : totalLogisticsWarningOrders;

        return (
            <TabItem
                tab={
                    <Spin spinning={loading}>
                        <span style={{ color: "#a94442" }}>发货未揽收预警</span>
                        <Badge
                            style={{ backgroundColor: "#777" }}
                            count={badgeCount}
                            overflowCount={Number.MAX_SAFE_INTEGER}
                        />
                        <PremiumVersionIcon style={{ position: "absolute", top: "-10px" }} />
                    </Spin>
                }
                key={
                    currentTab === OrderTab.PICKUP_DEADLINE_WARNING
                        ? OrderTab.PICKUP_DEADLINE_WARNING
                        : OrderTab.LOGISTICS_WARNING
                }
            />
        );
    }

    const getPendingPaymentText = () => {
        if (isJdDropshipShop(shop)) {
            return "未出库";
        }

        if ([ShopType.JD, ShopType.JD_PURCHASE].includes(shop?.type)) {
            return `${OrderTabNameMap[OrderTab.PENDING_PAYMENT]}/暂停`;
        }

        if ([ShopType.XIAOHONGSHU].includes(shop?.type)) {
            return `${OrderTabNameMap[OrderTab.PENDING_PAYMENT]}/拼团中`;
        }

        return OrderTabNameMap[OrderTab.PENDING_PAYMENT];
    };

    return (
        <Tabs
            className="order-tabs"
            type="card"
            activeKey={currentTab}
            onChange={onChange}
            tabBarExtraContent={tabBarExtraContent}
            items={buildTabsItems(
                <>
                    {![ShopType.MANUAL, ShopType.PDD_FULFILLMENT].includes(shopType) ? (
                        <TabItem tab={getPendingPaymentText()} key={OrderTab.PENDING_PAYMENT} />
                    ) : null}

                    <TabItem
                        tab={
                            <>
                                {OrderTabNameMap[OrderTab.PENDING_SHIPMENT]}
                                <Badge
                                    style={{ backgroundColor: "#777" }}
                                    count={totalPendingShipmentOrders}
                                    overflowCount={Number.MAX_SAFE_INTEGER}
                                />
                            </>
                        }
                        key={OrderTab.PENDING_SHIPMENT}
                    />

                    <TabItem
                        tab={
                            <>
                                {OrderTabNameMap[OrderTab.ON_HOLD]}
                                <Badge
                                    style={{ backgroundColor: "#777" }}
                                    count={totalOnHoldOrders}
                                    overflowCount={Number.MAX_SAFE_INTEGER}
                                />
                            </>
                        }
                        key={OrderTab.ON_HOLD}
                    />
                    {![ShopType.MANUAL, ShopType.PDD_FULFILLMENT, ShopType.INDEPENDENT_MANUAL].includes(shopType) && (
                        <TabItem
                            tab={
                                <>
                                    {
                                        OrderTabNameMap[
                                            currentTab === OrderTab.SHIPPED_REFUNDING
                                                ? OrderTab.SHIPPED_REFUNDING
                                                : OrderTab.PENDING_SHIPMENT_REFUNDING
                                        ]
                                    }
                                    <Badge
                                        style={{ backgroundColor: "#777" }}
                                        count={totalPendingShipmentRefundingOrders}
                                        overflowCount={Number.MAX_SAFE_INTEGER}
                                    />
                                </>
                            }
                            key={
                                currentTab === OrderTab.SHIPPED_REFUNDING
                                    ? OrderTab.SHIPPED_REFUNDING
                                    : OrderTab.PENDING_SHIPMENT_REFUNDING
                            }
                        />
                    )}
                    {isPreShipWhiteShop ? (
                        <TabItem
                            tab={
                                <>
                                    预发货
                                    <Badge
                                        style={{ backgroundColor: "#777" }}
                                        count={preShipOrders}
                                        overflowCount={Number.MAX_SAFE_INTEGER}
                                    />
                                </>
                            }
                            key={OrderTab.PRE_SHIP}
                        />
                    ) : null}

                    <TabItem
                        tab={shop.type === ShopType.PDD_FDS ? "已回传运单号" : OrderTabNameMap[OrderTab.SHIPPED]}
                        key={OrderTab.SHIPPED}
                    />

                    <TabItem tab={OrderTabNameMap[OrderTab.SUCCESS]} key={OrderTab.SUCCESS} />

                    {shopType !== ShopType.PDD_FULFILLMENT ? (
                        <TabItem tab={OrderTabNameMap[OrderTab.CANCELED]} key={OrderTab.CANCELED} />
                    ) : null}

                    {hasAbnormalOrders && (
                        <TabItem
                            tab={<span style={{ color: "#a94442" }}>{OrderTabNameMap[OrderTab.ABNORMAL]}</span>}
                            key={OrderTab.ABNORMAL}
                        />
                    )}

                    {buildLogisticsWarningTab()}
                </>,
            )}
        />
    );
};

export default observer(OrderTabs);
